import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Search, Filter } from "lucide-react";
import { normalizeFuelType, normalizeTransmission } from "../../lib/auction-utils";
import {
  CheckboxGroup,
  EmptyState,
  FilterSection,
  RangeFilterWithSlider,
} from "../../components/auction/FilterSection";
import { FilterDrawer } from "../../components/auction/FilterDrawer";
import { ManageAuctionCard } from "../../components/auction/ManageAuctionCard";
import type { UnifiedAuction } from "../../components/auction/AuctionCardShared";
import { useFavoriteAuctions } from "../../lib/use-favorite-auctions";

const TRANSPORT_REQUESTABLE_STATUSES = ["sold", "contract_pending", "invoice_pending", "payment_pending", "ready_for_pickup"];

export function MineKjopPage() {
  const { organizationSlug } = useParams<{ organizationSlug: string }>();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState<UnifiedAuction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [yearFrom, setYearFrom] = useState("");
  const [yearTo, setYearTo] = useState("");
  const [kmFrom, setKmFrom] = useState("");
  const [kmTo, setKmTo] = useState("");
  const [fuels, setFuels] = useState<string[]>([]);
  const [transmissions, setTransmissions] = useState<string[]>([]);
  const [makes, setMakes] = useState<string[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const { favorites, toggleFavorite } = useFavoriteAuctions();

  const fetchPurchases = () => {
    if (!organizationSlug) return;
    const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
    setLoading(true);
    fetch(`${api}/api/organizations/${organizationSlug}/purchases`, { credentials: "include" })
      .then((res) => {
        if (!res.ok) throw new Error("Kunne ikke laste kjøp");
        return res.json() as Promise<any[]>;
      })
      .then((data) => {
        const mapped = data
          .filter((p) => p.status !== "no_sale")
          .map((p) => {
            const v = p.vehicle;
            // Note: Backend /purchases typically returns simpler structure, but we might need to normalize
            return {
              ...p,
              primaryImage: p.primaryImage || v?.images?.find((i: any) => i.isPrimary) || v?.images?.[0] || null,
            };
          });
        setPurchases(mapped);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Ukjent feil"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPurchases();
  }, [organizationSlug]);

  const fuelOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of purchases) {
      const label = normalizeFuelType(p.vehicle?.fuelType ?? null);
      if (label !== "–") counts.set(label, (counts.get(label) ?? 0) + 1);
    }
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1])
      .map(([label, count]) => ({ value: label, label, count }));
  }, [purchases]);

  const transmissionOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of purchases) {
      const label = normalizeTransmission(p.vehicle?.transmission ?? null);
      if (label !== "–") counts.set(label, (counts.get(label) ?? 0) + 1);
    }
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1])
      .map(([label, count]) => ({ value: label, label, count }));
  }, [purchases]);

  const makeOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of purchases) {
      const make = p.vehicle?.make;
      if (make) counts.set(make, (counts.get(make) ?? 0) + 1);
    }
    return Array.from(counts.entries()).sort((a, b) => a[0].localeCompare(b[0]))
      .map(([label, count]) => ({ value: label, label, count }));
  }, [purchases]);

  const filtered = useMemo(() => {
    return purchases.filter((p) => {
      const v = p.vehicle;
      if (search) {
        const q = search.toLowerCase();
        const haystack = [p.title, v?.make, v?.model, v?.registrationNumber].join(" ").toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (yearFrom && v?.modelYear !== null && v?.modelYear !== undefined && v.modelYear < Number(yearFrom)) return false;
      if (yearTo && v?.modelYear !== null && v?.modelYear !== undefined && v.modelYear > Number(yearTo)) return false;
      if (kmFrom && v?.mileage !== null && v?.mileage !== undefined && v.mileage < Number(kmFrom)) return false;
      if (kmTo && v?.mileage !== null && v?.mileage !== undefined && v.mileage > Number(kmTo)) return false;
      if (fuels.length > 0 && !fuels.includes(normalizeFuelType(v?.fuelType ?? null))) return false;
      if (transmissions.length > 0 && !transmissions.includes(normalizeTransmission(v?.transmission ?? null))) return false;
      if (makes.length > 0 && (!v?.make || !makes.includes(v.make))) return false;
      return true;
    });
  }, [purchases, search, yearFrom, yearTo, kmFrom, kmTo, fuels, transmissions, makes]);

  const POST_SALE_STATUSES = ["sold", "contract_pending", "invoice_pending", "payment_pending", "ready_for_pickup", "picked_up", "in_transit", "delivered", "complaint_review", "pending_payout", "paid_out"];

  const waitingForAnswer = useMemo(
    () => filtered.filter((purchase) => purchase.status === "pending_seller_decision" || purchase.status === "negotiating"),
    [filtered],
  );

  const completedPurchases = useMemo(
    () => filtered.filter((purchase) => POST_SALE_STATUSES.includes(purchase.status)),
    [filtered],
  );

  function markTransportOrdered(id: string) {
    setPurchases((current) => current.map((purchase) => (
      purchase.id === id
        ? { ...purchase, transportOrdered: true }
        : purchase
    )));
  }

  function clearFilters() {
    setSearch("");
    setYearFrom("");
    setYearTo("");
    setKmFrom("");
    setKmTo("");
    setFuels([]);
    setTransmissions([]);
    setMakes([]);
  }

  const filterContent = (
    <>
      <div className="py-4" style={{ borderBottom: "1px solid var(--border)" }}>
        <h3 className="mb-3 text-sm font-semibold" style={{ color: "var(--foreground)" }}>
          Søk i Mine kjøp
        </h3>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Søk..." className="w-full rounded-xl py-2 pl-10 pr-4 text-sm bg-muted/50 border-none transition-all focus:bg-white focus:ring-2 focus:ring-accent/20"
          />
        </div>
      </div>

      <FilterSection title="Årsmodell">
        <RangeFilterWithSlider
          fromValue={yearFrom} toValue={yearTo} fromPlaceholder="Fra" toPlaceholder="Til"
          min={2000} max={new Date().getFullYear()}
          onChange={(from, to) => { setYearFrom(from); setYearTo(to); }}
        />
      </FilterSection>

      <FilterSection title="Kilometerstand">
        <RangeFilterWithSlider
          fromValue={kmFrom} toValue={kmTo} fromPlaceholder="Fra km" toPlaceholder="Til km"
          min={0} max={300000}
          onChange={(from, to) => { setKmFrom(from); setKmTo(to); }}
        />
      </FilterSection>

      {makeOptions.length > 0 && (
        <FilterSection title="Merke" defaultOpen={false}>
          <CheckboxGroup options={makeOptions} selected={makes} onChange={setMakes} />
        </FilterSection>
      )}

      {fuelOptions.length > 0 && (
        <FilterSection title="Drivstoff" defaultOpen={false}>
          <CheckboxGroup options={fuelOptions} selected={fuels} onChange={setFuels} />
        </FilterSection>
      )}

      {transmissionOptions.length > 0 && (
        <FilterSection title="Girkasse" defaultOpen={false}>
          <CheckboxGroup options={transmissionOptions} selected={transmissions} onChange={setTransmissions} />
        </FilterSection>
      )}
    </>
  );

  return (
    <div className="mx-auto flex max-w-screen-xl flex-col lg:flex-row gap-6 lg:gap-8 px-4 sm:px-6 py-4 sm:py-6">
      {/* Sidebar - Desktop only */}
      <aside className="hidden lg:block w-64 flex-shrink-0">
        <div className="sticky top-24 space-y-2">
          {filterContent}
        </div>
      </aside>

      {/* Filter Drawer - Mobile only */}
      <FilterDrawer
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onClear={clearFilters}
        title="Filtre"
        count={filtered.length}
      >
        {filterContent}
      </FilterDrawer>

      {/* Main */}
      <main className="min-w-0 flex-1">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <h1
              className="text-3xl font-black text-[#1a2e35]"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Mine kjøp
            </h1>
            <div className="mt-1 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {loading ? "Laster..." : (
                  <>Viser <strong>{filtered.length}</strong> av <strong>{purchases.length}</strong> biler</>
                )}
              </p>

              <button
                onClick={() => setIsFilterOpen(true)}
                className="flex items-center gap-2 rounded-xl border-2 border-primary bg-white px-4 py-2 text-sm font-bold shadow-sm transition-all active:scale-95 lg:hidden text-primary"
              >
                <Filter className="h-4 w-4" />
                Filter
              </button>
            </div>
          </div>
        </div>

        {error ? (
          <div className="flex h-40 items-center justify-center rounded-2xl bg-muted shadow-inner border border-border">
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        ) : loading ? (
          <div className="grid grid-cols-1 gap-4 sm:gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-64 animate-pulse rounded-2xl bg-muted" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            message={purchases.length === 0 ? "Ingen kjøp ennå" : "Ingen kjøp matcher filteret"}
            sub={purchases.length > 0 ? "Prøv å endre filterene dine." : undefined}
          />
        ) : (
          <div className="space-y-8">
            {waitingForAnswer.length > 0 && (
              <section className="space-y-4">
                <div>
                  <h2
                    className="text-xl font-black text-[#1a2e35]"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    Avventer svar fra selger
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Auksjoner du vant og som avventer neste steg.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:gap-6">
                  {waitingForAnswer.map((purchase) => (
                    <ManageAuctionCard
                      key={purchase.id}
                      auction={purchase}
                      organizationSlug={organizationSlug!}
                      mode="buyer"
                      isFavorite={favorites.includes(purchase.id)}
                      onToggleFavorite={toggleFavorite}
                      onRefresh={fetchPurchases}
                    />
                  ))}
                </div>
              </section>
            )}

            {completedPurchases.length > 0 && (
              <section className="space-y-4">
                {waitingForAnswer.length > 0 && (
                  <h2
                    className="text-xl font-black text-[#1a2e35]"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    Fullførte kjøp
                  </h2>
                )}
                <div className="grid grid-cols-1 gap-4 sm:gap-6">
                  {completedPurchases.map((purchase) => (
                    <ManageAuctionCard
                      key={purchase.id}
                      auction={purchase}
                      organizationSlug={organizationSlug!}
                      mode="buyer"
                      isFavorite={favorites.includes(purchase.id)}
                      onToggleFavorite={toggleFavorite}
                      onRefresh={fetchPurchases}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
